import { z } from 'zod';
// The statuses live with the register that owns them; the college edit is the
// second screen to need the list, and one list is the point (CLAUDE.md §3).
import { STUDENT_STATUS_ORDER } from '@/features/institute/students/constants';
import { instituteToday } from '@/features/teacher/utils';
import { ADDRESS_MAX_LENGTH } from './admission.schema';

/**
 * The college student form — admitting one, and correcting one afterwards.
 *
 * Mirrors `backend/src/modules/institute/admissions/collegeAdmission.validation.ts`.
 * The client checks the same rules for fast feedback; the server is the one
 * that decides (CLAUDE.md §6).
 *
 * A fraction of the institute form on purpose: a college admits an adult on their
 * own details. No guardian to ring, no class and section to sit in, no fee
 * record opened at the counter.
 *
 * Both screens are built from `collegeStudentShape`, for the reason the two
 * school screens share `admission.schema.ts` (CLAUDE.md §3): a middle name the
 * admission accepts and the edit rejects is a bug nobody finds until a student
 * asks for a correction. The edit adds their standing, which only exists once
 * they are on the roll.
 */

const NAME_PATTERN = /^[\p{L} .'-]+$/u;
/** Indian mobile numbers are ten digits and never begin below 6. */
const MOBILE_PATTERN = /^[6-9]\d{9}$/;

const name = (label: string) =>
  z
    .string()
    .trim()
    .min(1, `${label} is required`)
    .max(60, `${label} is too long`)
    .regex(NAME_PATTERN, "Letters, spaces and . ' - only");

const optional = (max: number) =>
  z
    .string()
    .trim()
    .max(max, 'That is too long')
    .optional()
    .or(z.literal(''))
    .transform((value) => value || undefined);

const collegeStudentShape = {
  firstName: name('The first name'),
  middleName: optional(60),
  lastName: name('The last name'),

  email: z.string().trim().toLowerCase().email('That is not a valid email address').max(150),
  mobile: z.string().trim().regex(MOBILE_PATTERN, 'Enter a 10-digit mobile number'),

  dateOfBirth: z
    .string()
    .optional()
    .or(z.literal(''))
    .transform((v) => v || undefined),
  gender: z
    .enum(['MALE', 'FEMALE', 'OTHER'])
    .optional()
    .or(z.literal(''))
    .transform((v) => v || undefined),
  admissionDate: z.string().min(1, 'An admission date is required'),
  addressLine1: optional(ADDRESS_MAX_LENGTH),
  city: optional(80),
  state: optional(60),
};

/** The dates have to agree with each other, so they are checked together. */
function applyCollegeStudentRules(values: CollegeStudentFields, ctx: z.RefinementCtx) {
  if (!values.dateOfBirth) return;

  const born = new Date(values.dateOfBirth);
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  if (born > today) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['dateOfBirth'],
      message: 'A date of birth cannot be in the future',
    });
  } else if (values.admissionDate && new Date(values.admissionDate) < born) {
    // Reported against the admission date: it is nearly always the typo.
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['admissionDate'],
      message: 'This is before the date of birth',
    });
  }
}

type CollegeStudentFields = z.infer<z.ZodObject<typeof collegeStudentShape>>;

export const collegeAdmissionSchema = z
  .object(collegeStudentShape)
  .superRefine(applyCollegeStudentRules);

/** The same details, plus where the student stands on the roll. */
export const collegeStudentEditSchema = z
  .object({
    ...collegeStudentShape,
    status: z.enum(STUDENT_STATUS_ORDER),
  })
  .superRefine(applyCollegeStudentRules);

export type CollegeAdmissionFormValues = z.input<typeof collegeAdmissionSchema>;
export type CollegeAdmissionPayload = z.output<typeof collegeAdmissionSchema>;
export type CollegeStudentEditValues = z.input<typeof collegeStudentEditSchema>;
export type CollegeStudentEditPayload = z.output<typeof collegeStudentEditSchema>;

/** A blank form. Today's date stands in for the admission date. */
export const EMPTY_COLLEGE_ADMISSION: CollegeAdmissionFormValues = {
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  mobile: '',
  dateOfBirth: '',
  gender: '',
  // The school's day: UTC would still be yesterday before 5:30 am IST.
  admissionDate: instituteToday(),
  addressLine1: '',
  city: '',
  state: '',
};
